import { useCallback, useEffect, useState } from 'react'
import { MutateBooleanCustomFieldValueInput } from 'schema'
import { CIMSBasicFieldProps } from '../../../CIMSBasicFields/types'
import { useProjectCustomFieldsContext } from '../../ProjectCustomFieldsContext'
import CIMSCheckbox from '../../../CIMSCheckbox'
import { useMultiLoading } from 'hooks/useMultiLoading'
import { useFoundCallback } from './useFoundCallback'
import { DisabledStates, BooleanValue } from '../../types'

export function useBooleanCustomFields({
  onCheck,
  booleanValues,
  alreadyDisabled,
  recordInactive,
}: UseBooleanCustomFieldsOptions) {
  const [fields, setFields] = useState<CIMSBasicFieldProps[]>([])
  const { renderedAsCheckboxes, disabledById } = useProjectCustomFieldsContext()
  const found = useFoundCallback()
  const { loading, addLoading, removeLoading } = useMultiLoading()
  const onChange = useCallback<UseBooleanCustomFieldsOptions['onCheck']>(
    async ({ customFieldId, value }) => {
      addLoading(customFieldId)
      await onCheck?.({ customFieldId, value: !value })
      removeLoading(customFieldId)
    },
    [addLoading, onCheck, removeLoading]
  )
  useEffect(() => {
    if (!renderedAsCheckboxes?.booleans) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Stop-gap for existing behavior.
    setFields(
      renderedAsCheckboxes?.booleans
        .reduce((reduced, boolean) => {
          const value = booleanValues?.find(
            ({ customFieldId }) => customFieldId === boolean.id
          )?.value
          reduced.push({
            customField: boolean,
            customFieldId: boolean.id,
            value: value ?? false,
          })
          return reduced
        }, Array<BooleanValue>())
        .map(({ customField, customFieldId, value }) => {
          const disabledOptions = disabledById({
            customFieldId,
            alreadyDisabled,
            recordInactive,
          })
          return found({
            customField,
            customFieldId,
            children: (
              <CIMSCheckbox
                checked={value}
                loading={loading(customFieldId)}
                onChange={async () => await onChange({ customFieldId, value })}
                disabled={disabledOptions}
              />
            ),
          })
        })
    )
  }, [
    renderedAsCheckboxes?.booleans,
    booleanValues,
    found,
    loading,
    onChange,
    disabledById,
    alreadyDisabled,
    recordInactive,
  ])
  return fields
}

export interface UseBooleanCustomFieldsOptions extends DisabledStates {
  booleanValues?: BooleanValue[]
  onCheck: (
    options: Required<MutateBooleanCustomFieldValueInput>
  ) => void | Promise<void>
}
